import { create } from 'zustand';
import { CurrencyCode } from '@/utils/currencyFormatter';
import { logger, LOG_CONTEXTS } from '@/utils/logger';

/**
 * Interface for the currency store state and actions
 * This store caches provider currencies from the database (provider.currency field)
 * Currency is fetched once per provider and reused throughout the app
 */
interface CurrencyState {
  // State - in-memory cache of provider currencies from database
  providerCurrencies: Record<number, CurrencyCode>; // Map of provider ID to currency code (from DB)
  defaultCurrency: CurrencyCode; // Default currency fallback

  // Actions
  setProviderCurrency: (providerId: number, currency: CurrencyCode) => void;
  getProviderCurrency: (providerId: number) => CurrencyCode;
  clearProviderCurrency: (providerId: number) => void;
  clearAllCurrencies: () => void;
}

/**
 * Create a currency store that caches provider currencies from the database
 * This is an in-memory cache - currency is fetched from provider.currency field and stored here
 * Components should call setProviderCurrency when provider data is loaded
 */
export const useCurrencyStore = create<CurrencyState>((set, get) => ({
  // Default state
  providerCurrencies: {},
  defaultCurrency: 'USD',

  // Actions

  /**
   * Set/cache the currency for a provider (from provider.currency database field)
   * This should be called when provider data is fetched
   */
  setProviderCurrency: (providerId: number, currency: CurrencyCode) => {
    logger.info(LOG_CONTEXTS.QUERY, 'Provider currency cached from database', { providerId, currency });
    set((state) => ({
      providerCurrencies: {
        ...state.providerCurrencies,
        [providerId]: currency
      }
    }));
  },

  /**
   * Get the currency for a provider from cache
   * Returns USD default if provider currency not cached yet
   */
  getProviderCurrency: (providerId: number) => {
    // Special case: fallback ID of -1 should always return the default currency
    if (providerId === -1 || !providerId) {
      return get().defaultCurrency;
    }

    const { providerCurrencies, defaultCurrency } = get();
    const currency = providerCurrencies[providerId];

    // If currency not cached, return default (USD)
    // Components should ensure provider currency is set when provider is loaded
    if (!currency) {
      logger.warn(LOG_CONTEXTS.QUERY, 'Provider currency not cached, using default', { providerId });
      return defaultCurrency;
    }

    return currency;
  },

  /**
   * Clear cached currency for a specific provider
   */
  clearProviderCurrency: (providerId: number) => {
    set((state) => {
      const { [providerId]: _, ...rest } = state.providerCurrencies;
      return { providerCurrencies: rest };
    });
  },

  /**
   * Clear all cached currencies (useful for logout/reset)
   */
  clearAllCurrencies: () => {
    set({ providerCurrencies: {} });
  }
}));

/**
 * Hook to get the current currency code for a specific provider
 * This returns the cached currency from the database
 * @param providerId - The ID of the provider
 * @returns The currency code for the provider (from cache or USD default)
 */
export const useProviderCurrency = (providerId: number): CurrencyCode => {
  return useCurrencyStore((state) => state.getProviderCurrency(providerId));
}; 